import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils.helpers";
import { useAuth } from "../contexts/AuthContext";
import { NomadeForm, ResidentForm } from "./PassesForms";

const toIso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const eur = (n) => `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d;
}

export default function PassesFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [passType, setPassType] = useState("nomade");
  const [nomadeRange, setNomadeRange] = useState({ from: undefined, to: undefined });
  const [residentMonths, setResidentMonths] = useState(1);
  const [residentStart, setResidentStart] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const nomadeDisabled = useMemo(() => {
    return (d) => {
      if (d < today) return true;
      if (nomadeRange.from && !nomadeRange.to) {
        const max = addMonths(nomadeRange.from, 2);
        if (d > max) return true;
      }
      return false;
    };
  }, [today, nomadeRange]);

  const residentEnd = useMemo(
    () => addMonths(residentStart, residentMonths),
    [residentStart, residentMonths]
  );

  const residentPricing = useMemo(() => {
    const base = 220 * residentMonths;
    if (residentMonths === 12) return { base, final: 2420, saving: base - 2420, freeMonth: true };
    return { base, final: base, saving: 0, freeMonth: false };
  }, [residentMonths]);

  const submit = async () => {
    if (!user) { navigate("/connexion?next=/reserver"); return; }
    setSubmitting(true);
    try {
      const payload = { pass_type: passType, payment_method: "card", origin_url: window.location.origin };
      if (passType === "nomade") {
        if (!nomadeRange.from || !nomadeRange.to) { toast.error("Sélectionnez une période"); setSubmitting(false); return; }
        payload.start_date = toIso(nomadeRange.from);
        payload.end_date = toIso(nomadeRange.to);
      } else {
        payload.start_date = toIso(residentStart);
        payload.duration_months = residentMonths;
      }
      const { data } = await api.post("/passes/purchase", payload);
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="grid grid-cols-2 gap-3 mb-8" data-testid="pass-type-toggle">
        <button onClick={() => setPassType("nomade")} data-testid="pass-nomade-tab"
          className={`p-4 rounded-2xl border-2 text-sm font-semibold transition ${passType === "nomade" ? "border-[#2B8B8E] bg-[#2B8B8E]/8" : "border-border"}`}>
          Pass Nomade
          <span className="block text-[10px] font-normal mt-1">Séjour libre jusqu'à 2 mois</span>
        </button>
        <button onClick={() => setPassType("resident")} data-testid="pass-resident-tab"
          className={`p-4 rounded-2xl border-2 text-sm font-semibold transition ${passType === "resident" ? "border-[#2B8B8E] bg-[#2B8B8E]/8" : "border-border"}`}>
          Pass Résident
          <span className="block text-[10px] font-normal mt-1">Abonnement 1 à 12 mois</span>
        </button>
      </div>

      {passType === "nomade" ? <NomadeForm range={nomadeRange} setRange={setNomadeRange} disabled={nomadeDisabled} /> : (
        <ResidentForm
          months={residentMonths} setMonths={setResidentMonths}
          start={residentStart} setStart={setResidentStart}
          endDate={residentEnd} pricing={residentPricing} today={today}
        />
      )}

      <div className="mt-8 flex flex-col items-start gap-3">
        <Button onClick={submit} disabled={submitting} data-testid="confirm-pass-button"
          className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full px-8">
          {submitting ? "Redirection…" : "Confirmer et payer"}
        </Button>
        <p className="text-xs text-muted-foreground">💳 Paiement par carte bancaire (Stripe)</p>
        {!user && <p className="text-xs text-muted-foreground">Vous serez invité à vous connecter</p>}
      </div>
    </div>
  );
}
